// src/pages/PrivacyPage.js
import React from 'react';
import './PrivacyPage.css';

const PrivacyPage = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: 20.05.2025</p>
        
        <section>
          <h2>1. Introduction</h2>
          <p>
            Klokify ("we", "our", or "us") respects your privacy and is committed to protecting your personal data. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application ("App").
          </p>
          <p>
            Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, 
            please do not access the App.
          </p>
        </section>

        <section>
          <h2>2. Data Controller</h2>
          <p>The data controller responsible for your personal data is:</p>
          <div className="contact-info">
            <p>Klokify<br />
            Tittmoninger Str. 17<br />
            84489 Burghausen<br />
            Germany</p>
            <p>Email: info@klokify.de</p>
          </div>
        </section>

        <section>
          <h2>3. Information We Collect</h2>
          
          <h3>3.1 Personal Data</h3>
          <p>We collect the following types of personal data:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, company name, employee ID</li>
            <li><strong>Profile Information:</strong> Profile pictures, job title, department, contact details</li>
            <li><strong>Time Tracking Data:</strong> Clock-in and clock-out times, work hours, breaks, overtime, schedules</li>
            <li><strong>Location Data:</strong> Geographic location at the time of clock-in and clock-out (with your permission only if Terminal Mode is deactivated)</li>
            <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, mobile network information</li>
            <li><strong>Usage Data:</strong> How you interact with our app and which features you use</li>
          </ul>

          <h3>3.2 Automatically Collected Data</h3>
          <p>When you use our App "Klokify", we automatically collect:</p>
          <ul>
            <li><strong>Technical Data:</strong> IP address, device information, operating system</li>
            <li><strong>Usage Patterns:</strong> How you interact with our App, which features you use most frequently</li>
            <li><strong>Location Data:</strong> Your geographic position when clocking in or out (if you've granted permission)</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Use Your Data</h2>
          <p>We use your data for the following purposes:</p>
          <ul>
            <li><strong>Providing Core Services:</strong> Enabling time tracking, schedule management, and reporting</li>
            <li><strong>Improving Our App:</strong> Understanding how users interact with the App to enhance functionality</li>
            <li><strong>Authentication:</strong> Verifying your identity and maintaining account security</li>
            <li><strong>Communication:</strong> Sending important notifications about your account or the App</li>
            <li><strong>Customer Support:</strong> Addressing your questions or concerns</li>
            <li><strong>Compliance:</strong> Meeting legal obligations related to employment law and time tracking requirements</li>
          </ul>
        </section>

        <section>
          <h2>5. Legal Basis for Processing</h2>
          <p>We process your personal data based on the following legal grounds:</p>
          <ul>
            <li><strong>Contract Performance:</strong> Processing necessary to provide you with our services</li>
            <li><strong>Legitimate Interests:</strong> Processing that serves our legitimate business interests</li>
            <li><strong>Legal Compliance:</strong> Processing necessary to comply with applicable laws</li>
            <li><strong>Consent:</strong> Processing based on your specific consent, which you can withdraw at any time</li>
          </ul>
        </section>

        <section>
          <h2>6. Data Sharing and Disclosure</h2>
          <p>We may share your information with:</p>
          <ul>
            <li><strong>Your Employer:</strong> As this is a workforce management solution, your employer will have access to your time tracking data</li>
            <li><strong>Service Providers:</strong> Third-party vendors who help us provide and improve our services</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental regulation</li>
          </ul>
          <p className="important">We do not sell your personal information to third parties.</p>
        </section>

        <section>
          <h2>7. Firebase Services</h2>
          <p>Our App uses Google Firebase for various functions, including:</p>
          <ul>
            <li><strong>Firebase Analytics:</strong> To understand app usage and performance</li>
            <li><strong>Firebase Authentication:</strong> To manage user authentication securely</li>
            <li><strong>Firestore Database:</strong> To store and synchronize app data</li>
            <li><strong>Firebase Cloud Messaging:</strong> To deliver notifications</li>
          </ul>
          <p>
            Firebase may collect and process data as described in Google's Privacy Policy: 
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"> https://policies.google.com/privacy</a>
          </p>
        </section>

        <section>
          <h2>8. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal data. 
            However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>9. Data Retention and Deletion</h2>
          <p>
            We retain your personal data only for as long as necessary to fulfill the purposes for which we collected it. 
            When you uninstall the App, certain data is automatically deleted from your device. 
            However, data stored on our servers will remain until you request deletion.
          </p>
          <p>To request deletion of your account and data, please contact <a href="mailto:info@klokify.de">info@klokify.de</a>.</p>
        </section>

        <section>
          <h2>10. Your Data Protection Rights</h2>
          <p>Depending on your location, you have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Correct inaccurate data</li>
            <li>Delete your data (in certain circumstances)</li>
            <li>Restrict or object to processing</li>
            <li>Data portability</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p>To exercise these rights, please contact <a href="mailto:info@klokify.de">info@klokify.de</a>.</p>
        </section>

        <section>
          <h2>11. International Data Transfers</h2>
          <p>
            Your data may be transferred to, and processed in, countries outside your country of residence. 
            We ensure that such transfers comply with applicable data protection laws.
          </p>
        </section>

        <section>
          <h2>12. Children's Privacy</h2>
          <p>
            Our App is not intended for children under the age of 16. 
            We do not knowingly collect personal information from children under 16.
          </p>
        </section>

        <section>
          <h2>13. App Tracking Transparency</h2>
          <p>
            In accordance with Apple's App Tracking Transparency framework, 
            we will request your permission before tracking your activity across other companies' apps and websites.
          </p>
        </section>

        <section>
          <h2>14. Offline Functionality</h2>
          <p>
            The App provides features that work offline. While using offline features, 
            the App will temporarily store necessary data on your device and sync with our servers 
            when an internet connection is re-established.
          </p>
        </section>

        <section>
          <h2>15. Changes to This Privacy Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. 
            The updated version will be indicated by an updated "Last Updated" date.
          </p>
        </section>

        <section>
          <h2>16. Contact Us</h2>
          <p>If you have questions about this Privacy Policy or our data practices, please contact:</p>
          <div className="contact-info">
            <p><strong>Klokify</strong><br />
            Email: <a href="mailto:info@klokify.de">info@klokify.de</a><br />
            Address: Tittmoninger Str. 17, 84489 Burghausen</p>
          </div>
        </section>

        <section>
          <h2>17. Supervisory Authority</h2>
          <p>
            You have the right to lodge a complaint with your local data protection authority 
            if you believe our processing of your personal data violates applicable law.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPage;